import React, { useCallback, useEffect, useRef, useState } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../ui/dialog";
import { toast } from "sonner";
import {
  Upload, Search, Trash2, Copy, Edit3, Image as ImageIcon,
  Check, X, Loader2, AlertTriangle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT = "image/png,image/jpeg,image/jpg,image/webp,image/gif";

function fmtBytes(n) {
  if (!n) return "0 B";
  const k = 1024;
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(n) / Math.log(k));
  return `${(n / Math.pow(k, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function absoluteUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${BACKEND_URL}${u}`;
}

function refKindLabel(kind) {
  if (kind === "website_page") return "Website pagina";
  if (kind === "trusted_brand") return "Trusted brand";
  if (kind === "homepage_route") return "Homepage route";
  return kind || "Anders";
}

/**
 * MediaLibrary — manage media files (upload / list / rename / delete).
 *
 * Props:
 *   pickMode  — when true, hides destructive actions and shows a "Selecteer"
 *               button per item that invokes `onSelect(item)`.
 *   onSelect  — callback fired with the selected media item.
 */
export default function MediaLibrary({ pickMode = false, onSelect }) {
  const { hasPermission, isAdminOrOwner } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [query, setQuery] = useState("");
  const [editId, setEditId] = useState(null);
  const [editAlt, setEditAlt] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [editFilename, setEditFilename] = useState("");
  const fileInputRef = useRef(null);

  // Delete dialog state
  const [deleteTarget, setDeleteTarget] = useState(null); // item or null
  const [deleteRefs, setDeleteRefs] = useState(null);     // { in_use, references } or null while loading
  const [deleteBusy, setDeleteBusy] = useState(false);

  const canWrite = hasPermission("website:edit") || hasPermission("admin:all") || hasPermission("settings:edit");
  const canForceDelete = isAdminOrOwner();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/media${query ? `?q=${encodeURIComponent(query)}` : ""}`);
      setItems(Array.isArray(r.data?.items) ? r.data.items : []);
    } catch (e) {
      toast.error(formatApiError(e, "Kon media library niet laden"));
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { load(); }, [load]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error("Bestand is groter dan 5 MB");
      e.target.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("alt_text", "");
    fd.append("title", "");
    setUploading(true);
    try {
      const r = await adminApi.post("/admin/media/upload", fd, {
        // Let axios + the browser set the correct multipart boundary
        // automatically by deleting the default JSON header for this call.
        headers: { "Content-Type": undefined },
      });
      toast.success("Afbeelding geüpload");
      setItems((s) => [r.data, ...s]);
      if (pickMode && onSelect) onSelect(r.data);
    } catch (err) {
      toast.error(formatApiError(err, "Upload mislukt"));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onCopy = async (url) => {
    try {
      await navigator.clipboard.writeText(absoluteUrl(url));
      toast.success("URL gekopieerd");
    } catch {
      toast.error("Kon URL niet kopieren");
    }
  };

  // ---- Delete flow with reference check ----
  const openDelete = async (item) => {
    setDeleteTarget(item);
    setDeleteRefs(null);
    try {
      const r = await adminApi.get(`/admin/media/${item.id}/references`);
      setDeleteRefs(r.data);
    } catch (e) {
      toast.error(formatApiError(e, "Kon referenties niet ophalen"));
      setDeleteRefs({ in_use: false, references: [] });
    }
  };

  const closeDelete = () => {
    if (deleteBusy) return;
    setDeleteTarget(null);
    setDeleteRefs(null);
  };

  const confirmDelete = async (force = false) => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      const url = `/admin/media/${deleteTarget.id}${force ? "?force=true" : ""}`;
      const r = await adminApi.delete(url);
      setItems((s) => s.filter((it) => it.id !== deleteTarget.id));
      if (r.data?.had_references && force) {
        toast.success("Afbeelding verwijderd en gekoppelde verwijzingen zijn opgeschoond.");
      } else {
        toast.success("Afbeelding verwijderd");
      }
      setDeleteTarget(null);
      setDeleteRefs(null);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && detail && typeof detail === "object" && Array.isArray(detail.references)) {
        // Server says it's in use — surface the references the user didn't see yet
        setDeleteRefs({ in_use: true, references: detail.references });
        toast.error("Deze afbeelding wordt nog gebruikt en kan niet worden verwijderd.");
      } else {
        toast.error(formatApiError(e, "Verwijderen mislukt"));
      }
    } finally {
      setDeleteBusy(false);
    }
  };

  const openEdit = (item) => {
    setEditId(item.id);
    setEditAlt(item.alt_text || "");
    setEditTitle(item.title || "");
    setEditFilename(item.filename || "");
  };
  const cancelEdit = () => { setEditId(null); setEditAlt(""); setEditTitle(""); setEditFilename(""); };

  const saveEdit = async (item) => {
    try {
      const r = await adminApi.patch(`/admin/media/${item.id}`, {
        alt_text: editAlt,
        title: editTitle,
        filename: editFilename,
      });
      setItems((s) => s.map((it) => (it.id === item.id ? r.data : it)));
      toast.success("Opgeslagen");
      cancelEdit();
    } catch (e) {
      toast.error(formatApiError(e, "Opslaan mislukt"));
    }
  };

  const inUse = !!deleteRefs?.in_use;
  const refsList = deleteRefs?.references || [];

  return (
    <div data-testid="media-library">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Zoek op bestandsnaam of alt-tekst..."
            className="pl-9 rounded-xl"
            data-testid="media-search"
          />
        </div>
        {canWrite && (
          <>
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-brand-500 hover:bg-brand-600 text-white rounded-xl font-chivo font-bold uppercase text-xs"
              data-testid="media-upload-button"
            >
              {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Bezig...</> : <><Upload className="w-4 h-4 mr-2" />Upload afbeelding</>}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT}
              hidden
              onChange={onUpload}
              data-testid="media-upload-input"
            />
          </>
        )}
        <span className="text-xs text-gray-500">Max 5 MB · PNG, JPG, WEBP, GIF</span>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="py-16 text-center text-gray-400 text-sm" data-testid="media-loading">Laden...</div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center text-gray-500 border border-dashed border-gray-300 rounded-2xl" data-testid="media-empty">
          <ImageIcon className="w-10 h-10 mx-auto text-gray-300 mb-3" />
          <p className="font-chivo font-bold text-[#082c50]">Nog geen afbeeldingen</p>
          <p className="text-xs mt-1">Klik op "Upload afbeelding" om uw eerste foto toe te voegen.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="media-grid">
          {items.map((it) => {
            const url = absoluteUrl(it.url);
            const isEditing = editId === it.id;
            return (
              <div key={it.id} className="bg-white border border-gray-200 rounded-2xl overflow-hidden flex flex-col" data-testid={`media-item-${it.id}`}>
                <div className="aspect-square bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center overflow-hidden">
                  <img
                    src={url}
                    alt={it.alt_text || it.filename}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                </div>
                {isEditing ? (
                  <div className="p-3 space-y-2">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wide font-bold">Bestandsnaam</Label>
                      <Input value={editFilename} onChange={(e) => setEditFilename(e.target.value)} className="rounded-xl text-xs" data-testid={`media-edit-filename-${it.id}`} />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wide font-bold">Alt-tekst</Label>
                      <Input value={editAlt} onChange={(e) => setEditAlt(e.target.value)} className="rounded-xl text-xs" data-testid={`media-edit-alt-${it.id}`} />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wide font-bold">Title</Label>
                      <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="rounded-xl text-xs" data-testid={`media-edit-title-${it.id}`} />
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" onClick={() => saveEdit(it)} className="flex-1 bg-brand-500 hover:bg-brand-600 text-white rounded-xl text-xs" data-testid={`media-save-${it.id}`}>
                        <Check className="w-3.5 h-3.5 mr-1" />Opslaan
                      </Button>
                      <Button size="sm" variant="outline" onClick={cancelEdit} className="rounded-xl text-xs">
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 flex-1 flex flex-col">
                    <div className="text-xs font-chivo font-bold text-[#082c50] truncate" title={it.filename}>{it.filename}</div>
                    <div className="text-[10px] text-gray-400 mt-0.5">
                      {it.width && it.height ? `${it.width}×${it.height}` : ""}
                      {it.width && " · "}
                      {fmtBytes(it.size_bytes)}
                    </div>
                    {it.alt_text && <div className="text-[10px] text-gray-600 mt-1 line-clamp-1" title={it.alt_text}>Alt: {it.alt_text}</div>}
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {pickMode ? (
                        <Button size="sm" onClick={() => onSelect?.(it)} className="flex-1 bg-brand-500 hover:bg-brand-600 text-white rounded-xl text-xs h-7" data-testid={`media-select-${it.id}`}>
                          Selecteer
                        </Button>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => onCopy(it.url)}
                            className="flex-1 text-[10px] font-bold uppercase tracking-wide bg-gray-100 hover:bg-gray-200 text-[#082c50] rounded-xl py-1.5 inline-flex items-center justify-center gap-1"
                            data-testid={`media-copy-${it.id}`}
                          >
                            <Copy className="w-3 h-3" />URL
                          </button>
                          {canWrite && (
                            <>
                              <button
                                type="button"
                                onClick={() => openEdit(it)}
                                className="text-[10px] font-bold uppercase tracking-wide bg-gray-100 hover:bg-gray-200 text-[#082c50] rounded-xl py-1.5 px-2 inline-flex items-center justify-center"
                                data-testid={`media-edit-${it.id}`}
                                title="Bewerken"
                              >
                                <Edit3 className="w-3 h-3" />
                              </button>
                              <button
                                type="button"
                                onClick={() => openDelete(it)}
                                className="text-[10px] font-bold uppercase tracking-wide bg-red-50 hover:bg-red-100 text-red-700 rounded-xl py-1.5 px-2 inline-flex items-center justify-center"
                                data-testid={`media-delete-${it.id}`}
                                title="Verwijderen"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Delete confirmation dialog with reference check */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) closeDelete(); }}>
        <DialogContent
          className="max-w-lg"
          data-testid="media-delete-dialog"
          onInteractOutside={(e) => { if (deleteBusy) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#082c50]">
              {inUse ? (
                <>
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  Afbeelding wordt nog gebruikt
                </>
              ) : (
                <>
                  <Trash2 className="w-5 h-5 text-red-600" />
                  Afbeelding verwijderen?
                </>
              )}
            </DialogTitle>
            <DialogDescription className="pt-2">
              {!deleteRefs ? (
                <span className="inline-flex items-center gap-2 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" /> Referenties controleren...
                </span>
              ) : inUse ? (
                <>
                  Deze afbeelding wordt nog gebruikt op de onderstaande plek(ken).
                  Bij <strong>Toch verwijderen</strong> worden de verwijzingen automatisch
                  leeggemaakt zodat er geen kapotte afbeeldingen ontstaan op de publieke site.
                </>
              ) : (
                "Weet u zeker dat u deze afbeelding wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt."
              )}
            </DialogDescription>
          </DialogHeader>

          {/* Preview row */}
          {deleteTarget && (
            <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl p-3">
              <div className="w-14 h-14 rounded-lg overflow-hidden bg-white border border-slate-200 flex-shrink-0">
                <img
                  src={absoluteUrl(deleteTarget.url)}
                  alt={deleteTarget.alt_text || deleteTarget.filename}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-chivo font-bold text-[#082c50] truncate" data-testid="media-delete-filename">
                  {deleteTarget.filename}
                </div>
                {deleteTarget.title && (
                  <div className="text-xs text-gray-600 truncate">Title: {deleteTarget.title}</div>
                )}
                {deleteTarget.alt_text && (
                  <div className="text-xs text-gray-500 truncate">Alt: {deleteTarget.alt_text}</div>
                )}
              </div>
            </div>
          )}

          {/* References list */}
          {inUse && (
            <div
              className="border border-amber-200 bg-amber-50 rounded-xl p-3 max-h-60 overflow-auto"
              data-testid="media-delete-references"
            >
              <div className="text-xs font-bold text-amber-900 uppercase tracking-wide mb-2">
                Gebruikt op {refsList.length} plek{refsList.length === 1 ? "" : "ken"}:
              </div>
              <ul className="space-y-1.5">
                {refsList.map((r, i) => (
                  <li key={i} className="text-sm text-amber-900 flex items-start gap-2" data-testid={`media-delete-ref-${i}`}>
                    <span className="inline-block flex-shrink-0 mt-0.5 px-1.5 py-0.5 text-[10px] uppercase font-bold rounded bg-amber-200 text-amber-900">
                      {refKindLabel(r.kind)}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="font-semibold">{r.label}</span>
                      {r.field && <span className="text-amber-800/80"> — {r.field}</span>}
                    </span>
                  </li>
                ))}
              </ul>
              {!canForceDelete && (
                <div className="mt-3 text-xs text-amber-900 border-t border-amber-200 pt-2">
                  Alleen Owner of Admin mag deze afbeelding geforceerd verwijderen.
                  Verwijder de verwijzingen handmatig of vraag een Admin om hulp.
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={closeDelete}
              disabled={deleteBusy}
              className="rounded-xl"
              data-testid="media-delete-cancel"
            >
              Annuleren
            </Button>
            {deleteRefs && !inUse && (
              <Button
                onClick={() => confirmDelete(false)}
                disabled={deleteBusy}
                className="rounded-xl bg-red-600 hover:bg-red-700 text-white"
                data-testid="media-delete-confirm"
              >
                {deleteBusy ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verwijderen...</>
                ) : (
                  <><Trash2 className="w-4 h-4 mr-2" />Verwijderen</>
                )}
              </Button>
            )}
            {deleteRefs && inUse && canForceDelete && (
              <Button
                onClick={() => confirmDelete(true)}
                disabled={deleteBusy}
                className="rounded-xl bg-red-600 hover:bg-red-700 text-white"
                data-testid="media-delete-force-confirm"
              >
                {deleteBusy ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verwijderen...</>
                ) : (
                  <><Trash2 className="w-4 h-4 mr-2" />Toch verwijderen</>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
